import { createSelector } from 'reselect'
import type { ArtistId } from 'types/artist'
import type {
  ClientLibrary,
  ClientLibraryArtist,
  ClientLibraryId,
  ClientLibraryPlaylist,
} from 'types/client-library'
import type { PlaylistId } from 'types/playlist'
import type { State } from 'types/redux'
import type { ClientLibrariesState } from '@disco/state-management/reducers/client-libraries'
import { getArtists } from '@disco/state-management/selectors/artists'
import { getPlaylists } from '@disco/state-management/selectors/playlists'
import { getTracks } from '@disco/state-management/selectors/tracks'
import { omit } from '@disco/utils/array'
/**
 * Gets all client libraries with loading state.
 * @param state - The Redux state
 * @returns Object containing loading status and array of client libraries
 */
export const getClientLibraries = (
  state: State,
): {
  loading: boolean
  loaded: boolean
  clientLibraries: Array<ClientLibrary>
} => ({
  ...state.clientLibraries.libraries,
  clientLibraries: Object.values(state.clientLibraries.libraries.items),
})
/**
 * Gets a specific client library by ID.
 * @param state - The Redux state
 * @param libraryId - The client library ID to retrieve
 * @returns The client library object, or null/undefined if not found
 */
export const getClientLibrary = (
  state: State,
  libraryId: ClientLibraryId,
): ClientLibrary | null | undefined =>
  state.clientLibraries.libraries.items[libraryId]
/**
 * Gets client library albums with playlist data merged.
 * @param state - The Redux state
 * @param libraryId - The client library ID
 * @returns Client library albums with full playlist objects, or null if no albums
 */
export const getClientLibraryAlbums = createSelector(
  [getClientLibrary, getPlaylists],
  (
    library: ReturnType<typeof getClientLibrary>,
    playlists: ReturnType<typeof getPlaylists>,
  ) =>
    library?.albums
      ? {
          ...library.albums,
          items: Object.keys(library.albums.items).reduce(
            (
              items: Record<PlaylistId, Partial<ClientLibraryPlaylist>>,
              key: string,
            ) => {
              const playlistId = Number(key)
              return {
                ...items,
                [playlistId]: {
                  ...playlists[playlistId],
                  ...library?.albums?.items[playlistId],
                },
              }
            },
            {},
          ),
        }
      : null,
)
/**
 * Gets client library artists with artist data merged.
 * @param state - The Redux state
 * @param libraryId - The client library ID
 * @returns Client library artists with full artist objects, or null if no artists
 */
export const getClientLibraryArtists = createSelector(
  [getClientLibrary, getArtists],
  (
    library: ReturnType<typeof getClientLibrary>,
    artists: ReturnType<typeof getArtists>,
  ) =>
    library?.artists
      ? {
          ...library.artists,
          invalidate: artists.invalidate,
          items: Object.keys(library.artists.items).reduce(
            (
              items: Record<ArtistId, Partial<ClientLibraryArtist>>,
              key: string,
            ) => {
              const artistId = Number(key)
              return {
                ...items,
                [artistId]: {
                  ...omit(artists.items[artistId], ['tracks']),
                  ...library?.artists?.items[artistId],
                },
              }
            },
            {},
          ),
        }
      : null,
)
/**
 * Gets a specific artist from a client library.
 * @param state - The Redux state
 * @param libraryId - The client library ID
 * @param artistId - The artist ID to retrieve
 * @returns The client library artist object, or null if not found
 */
export const getClientLibraryArtist = createSelector(
  getClientLibraryArtists,
  (_state, _libraryId, artistId) => artistId,
  (artists: ReturnType<typeof getClientLibraryArtists>, artistId: ArtistId) =>
    artists ? artists.items[artistId] : null,
)
/**
 * Gets tracks for a specific artist in a client library.
 * @param state - The Redux state
 * @param libraryId - The client library ID
 * @param artistId - The artist ID
 * @returns Artist tracks with full track objects, or null if no tracks
 */
export const getClientLibraryArtistTracks = createSelector(
  [getClientLibraryArtist, getTracks],
  (
    artist: ReturnType<typeof getClientLibraryArtist>,
    tracks: ReturnType<typeof getTracks>,
  ) =>
    artist?.tracks
      ? {
          ...artist.tracks,
          items: Object.keys(artist.tracks.items).reduce((items, key) => {
            const trackId = Number(key)
            return {
              ...items,
              [trackId]: {
                ...tracks[trackId],
                ...artist?.tracks?.items[trackId],
              },
            }
          }, {}),
        }
      : null,
)
/**
 * Gets client library playlists with playlist data merged.
 * @param state - The Redux state
 * @param libraryId - The client library ID
 * @returns Client library playlists with full playlist objects, or null if no playlists
 */
export const getClientLibraryPlaylists = createSelector(
  [getClientLibrary, getPlaylists],
  (
    library: ReturnType<typeof getClientLibrary>,
    playlists: ReturnType<typeof getPlaylists>,
  ) =>
    library?.playlists
      ? {
          ...library.playlists,
          items: Object.keys(library.playlists.items).reduce(
            (items: Record<PlaylistId, ClientLibraryPlaylist>, key: string) => {
              const playlistId = Number(key)
              return {
                ...items,
                [playlistId]: {
                  ...playlists[playlistId],
                  ...library?.playlists?.items[playlistId],
                },
              }
            },
            {},
          ),
        }
      : null,
)
/**
 * Gets client library tracks with track data merged, including all and featured track lists.
 * @param state - The Redux state
 * @param libraryId - The client library ID
 * @returns Client library tracks with full track objects, or null if no tracks
 * @todo Optimize nested selection
 */
export const getClientLibraryTracks = createSelector(
  [getClientLibrary, getTracks],
  (
    library: ReturnType<typeof getClientLibrary>,
    tracks: ReturnType<typeof getTracks>,
  ) =>
    library
      ? {
          ...library.tracks,
          items: Object.keys(library.tracks?.items ?? {}).reduce(
            (items, trackId) => ({
              ...items,
              [trackId]: {
                ...tracks[parseInt(trackId, 10)],

                ...library?.tracks?.items[trackId],
              },
            }),
            {},
          ),
          all: {
            ...library.tracks?.all,
            list:
              library &&
              library.tracks &&
              library.tracks.all &&
              library.tracks.all.list.map(trackId => ({
                ...tracks[trackId],
                ...library?.tracks?.items[trackId],
              })),
          },
          featured: {
            ...library.tracks?.featured,
            list:
              library &&
              library.tracks &&
              library.tracks.featured &&
              library.tracks.featured.list.map(trackId => ({
                ...tracks[trackId],
                ...library?.tracks?.items[trackId],
              })),
          },
        }
      : null,
)
/**
 * Gets the clients list for a client library.
 * @param state - The Redux state
 * @param libraryId - The client library ID
 * @returns The clients object for the library, or null if library not found
 */
export const getClientLibraryClients = createSelector(
  getClientLibrary,
  (library: ReturnType<typeof getClientLibrary>) =>
    library ? library.clients : null,
)
/**
 * Gets a specific client from a client library.
 * @param state - The Redux state
 * @param libraryId - The client library ID
 * @param clientId - The client ID to retrieve
 * @returns The client object, or null if not found
 */
export const getClientLibraryClient = createSelector(
  getClientLibraryClients,
  (_state, _libraryId, clientId) => clientId,
  (clients: ReturnType<typeof getClientLibraryClients>, clientId: number) => {
    const client = clients?.list?.find(
      (client: any) => client['id'] === clientId,
    )
    return client ?? null
  },
)
/**
 * Gets the discovery panel state.
 * @param state - The Redux state
 * @returns The discovery panel state object
 */
export const getDiscoveryPanel = (
  state: State,
): ClientLibrariesState['discoveryPanel'] =>
  state.clientLibraries.discoveryPanel
/**
 * Gets the discovery panel favorite items state.
 * @param state - The Redux state
 * @returns The discovery panel favorite state object
 */
export const getDiscoveryPanelFavorite = (
  state: State,
): ClientLibrariesState['discoveryPanelFavorite'] =>
  state.clientLibraries.discoveryPanelFavorite
/**
 * Gets the discovery panel recent items state.
 * @param state - The Redux state
 * @returns The discovery panel recent state object
 */
export const getDiscoveryPaneRecentLoad = (
  state: State,
): ClientLibrariesState['discoveryPanelRecent'] =>
  state.clientLibraries.discoveryPanelRecent
/**
 * Gets the discovery panel set state.
 * @param state - The Redux state
 * @returns The discovery panel set state object
 */
export const getDiscoveryPanelSet = (
  state: State,
): ClientLibrariesState['discoveryPanelSet'] =>
  state.clientLibraries.discoveryPanelSet
/**
 * Gets the suggested libraries state.
 * @param state - The Redux state
 * @returns The suggested libraries state object
 */
export const getSuggestedLibraries = (
  state: State,
): ClientLibrariesState['suggestedLibraries'] =>
  state.clientLibraries.suggestedLibraries
/**
 * Gets the primary client library from all available libraries.
 * @param state - The Redux state
 * @returns The primary client library, or undefined if none is marked as primary
 */
export const getPrimaryClientLibrary = createSelector(
  [getClientLibraries],
  ({ clientLibraries }: ReturnType<typeof getClientLibraries>) =>
    clientLibraries.find(library => Boolean(library?.primary)),
)
